import type { CSSProperties } from 'react';

/**
 * Pure vendor-branching logic for `<IrisEnabledBanner>`.
 *
 * Extracted so the Iris/Hive copy + styling decisions can be unit-tested
 * without a React renderer or mocks of the `@studio/design-system/themes`
 * module — the JSX in the component is now just a thin shell around the
 * struct this function returns.
 *
 * Per the `ai-code-requires-tests` rule (global).
 */

/** User-facing copy constants. Exported so tests can assert defaults. */
export const IRIS_TEXT =
  'Iris-enabled devices are auto-configured, pre-calibrated, and remotely controllable worldwide.';

export const HIVE_TITLE = 'This is a Hive Linked Device';
export const HIVE_TEXT =
  'Hive Linked devices are automatically configured, pre-calibrated, and controllable from anywhere in the world.';

export const IRIS_GRADIENT =
  'linear-gradient(135deg, rgba(255, 80, 95, 1) 2%, rgba(54, 100, 255, 1) 56%, rgba(41, 204, 106, 1) 100%)';

export const IRIS_LOGO_SRC = '/assets/icons/iris_badge_no_bg.png';

/**
 * Inputs to `resolveBannerDisplay` — everything the caller can override.
 *
 * The override props use `T | undefined` rather than plain `T?` because
 * the tsconfig enables `exactOptionalPropertyTypes`, which makes `foo?: T`
 * reject an explicit `undefined` value. Callers destructure their own
 * optional props (which ARE `T | undefined`) and pass them through, so
 * this interface must accept `undefined` too.
 */
export interface BannerInputs {
  /** True for the Iris vendor theme, false for Hive. */
  isIris: boolean;
  /** Fallback logo when `logoSrc` is omitted — typically `getCurrentVendorBadgeIcon()`. */
  fallbackLogoSrc: string;
  /** Fallback logo alt when `logoAlt` is omitted — typically `${vendorName()} Logo`. */
  fallbackLogoAlt: string;
  /** Caller override for the logo image URL. */
  logoSrc?: string | undefined;
  /** Caller override for the logo alt text. */
  logoAlt?: string | undefined;
  /** Caller override for the banner title. */
  title?: string | undefined;
  /** Caller override for the banner body text. */
  text?: string | undefined;
}

/** Output of `resolveBannerDisplay` — the exact values the JSX renders. */
export interface BannerDisplay {
  displayLogoSrc: string;
  displayLogoAlt: string;
  /** Undefined means "don't render the title line" — only Iris gets this by default. */
  displayTitle: string | undefined;
  displayText: string;
  /** Inline `style` for the outer wrapper (gradient padding for Iris, solid border for Hive). */
  borderStyle: CSSProperties;
  /** Tailwind class string for the inner dark background. */
  innerBg: string;
}

/**
 * Resolve the banner's display values from the current vendor + caller overrides.
 *
 * Iris: rainbow gradient border, no title, single-line body.
 * Hive: solid orange border, title + body.
 * Caller props always win over vendor defaults.
 */
export const resolveBannerDisplay = (inputs: BannerInputs): BannerDisplay => {
  const { isIris, fallbackLogoSrc, fallbackLogoAlt, logoSrc, logoAlt, title, text } = inputs;

  return {
    displayLogoSrc: logoSrc ?? (isIris ? IRIS_LOGO_SRC : fallbackLogoSrc),
    displayLogoAlt: logoAlt ?? fallbackLogoAlt,
    // Iris uses a single-line body (no title). Hive renders both.
    displayTitle: title ?? (isIris ? undefined : HIVE_TITLE),
    displayText: text ?? (isIris ? IRIS_TEXT : HIVE_TEXT),
    borderStyle: isIris
      ? { background: IRIS_GRADIENT, padding: '1px' }
      : { border: '1px solid #F47F00' },
    innerBg: isIris ? 'bg-[#22293E]' : 'bg-[#252526]'
  };
};
